import React from 'react';
import { useContent } from '../hooks/useContent';
import { useScrollToTop } from '../hooks/useScrollToTop';
import SEO from '../components/SEO';
import LocaleLink from '../components/LocaleLink';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  PageContainer,
  Container,
  HeroContent,
  HeroTitle,
  ContentText,
  ContentSection,
  BulletList,
  InfoTitle,
  staggerContainer,
  staggerItem,
  heroStaggerContainerVariants,
  heroStaggerItemVariants,
  CardArrowIcon,
  LazyBackgroundImage,
  SolutionsHeroSection,
  SectionDivider,
} from '../components/shared/SharedComponents';

/**
 * Left-aligned hero; outer spacing matches Home.js (section padding + Container),
 * not SolutionsHeroSection + HeroContentWrapper (which sat too tight to top/edges).
 */
const StrategicHeroSection = styled(SolutionsHeroSection)`
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  padding: 140px 0 80px;

  @media (max-width: 768px) {
    align-items: stretch;
    padding: 110px 0 48px;
    min-height: auto;
  }
`;

const StrategicHeroContent = styled(HeroContent)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  text-align: left;
  width: 100%;
`;

const CategoryCardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  margin-top: 32px;
`;

const CategoryCard = styled(LocaleLink)`
  display: flex;
  flex-direction: column;
  padding: 32px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 24px;
  border: 1px solid rgba(226, 232, 240, 0.6);
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.08);
  text-decoration: none;
  color: inherit;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;

  &:hover {
    box-shadow: 0 20px 40px rgba(15, 23, 42, 0.12);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const CategoryCardTitle = styled.h4`
  font-size: clamp(20px, 2.5vw, 22px);
  font-weight: 700;
  color: #1d334e;
  margin: 0 0 12px 0;
  line-height: 1.3;
`;

const CategoryCardIntro = styled.p`
  font-size: 15px;
  color: rgba(29, 51, 78, 0.75);
  line-height: 1.6;
  margin: 0 0 12px 0;

  &:last-of-type {
    margin-bottom: 24px;
  }
`;

const CategoryCardTwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
    margin-bottom: 20px;
  }
`;

const CategoryCardCol = styled.div`
  min-width: 0;
`;

const CategoryCardSectionTitle = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: #1d334e;
  letter-spacing: 0.02em;
  margin: 0 0 12px 0;
  line-height: 1.3;
`;

const CategoryCardBulletList = styled.ul`
  margin: 0;
  padding-left: 20px;
  list-style: none;

  li {
    font-size: 14px;
    color: rgba(29, 51, 78, 0.75);
    margin-bottom: 6px;
    line-height: 1.5;
    position: relative;
    padding-left: 4px;

    &::before {
      content: '';
      position: absolute;
      left: -16px;
      top: 0.55em;
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: #BF1922;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const CategoryCardPublicationsList = styled.ul`
  margin: 0;
  padding-left: 0;
  list-style: none;

  li {
    font-size: 14px;
    color: rgba(29, 51, 78, 0.75);
    margin-bottom: 8px;
    line-height: 1.5;
    position: relative;
    padding-left: 16px;

    &::before {
      content: '→';
      position: absolute;
      left: 0;
      color: #BF1922;
      font-weight: 600;
      font-size: 12px;
    }
  }
`;

/** Opts out of ContentText global dot bullets — arrows only (see SharedComponents ContentText ul rule). */
const CenteredDomainLinksList = styled.ul.attrs({ 'data-list-variant': 'arrow-links' })`
  margin: 0 0 32px 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;

  li {
    display: flex;
    align-items: left;
    font-size: clamp(15px, 2vw, 17px);
    color: rgba(29, 51, 78, 0.85);
    line-height: 1.5;
  }

  a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  a::before {
    content: '→';
    color: #BF1922;
    font-weight: 600;
    font-size: 14px;
    line-height: 1;
  }
`;

const BulletLink = styled(LocaleLink)`
  color: inherit;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #BF1922;
  }
`;

const CategoryCardButton = styled.span`
  display: inline-flex;
  align-items: center;
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid rgba(226, 232, 240, 0.8);
  color: #BF1922;
  font-size: 15px;
  font-weight: 600;
  transition: color 0.2s ease;

  svg {
    margin-left: 6px;
  }

  ${CategoryCard}:hover & {
    color: #8B1419;
  }
`;

const STRATEGIC_OVERVIEW_TITLES = new Set(['Strategic Intelligence Overview', '战略情报概览']);

const StrategicIntelligence = () => {
  useScrollToTop();
  const { getPageContent } = useContent();
  const page = getPageContent('strategicIntelligence');
  const sc = page?.sectionContent;
  const blocks = sc?.blocks ?? [];
  const heroImage = page?.heroImage || '/intelligence/office-building-2.png';
  const title = page?.title ?? 'Strategic Intelligence';
  const subtitle = page?.subtitle;

  const renderBlock = (block, index) => {
    if (block.type === 'intro') {
      const paragraphs = block.paragraphs ?? [];
      if (!paragraphs.length) return null;
      return (
        <ContentSection key={index}>
          <ContentText as={motion.div} variants={staggerItem}>
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </ContentText>
        </ContentSection>
      );
    }
    if (block.type === 'bulletBlock') {
      const blockTitle = block.title != null ? String(block.title).trim() : '';
      const showOverviewDivider = STRATEGIC_OVERVIEW_TITLES.has(blockTitle);
      const hasBullets = (block.bullets ?? []).length > 0;
      const hasLinks = (block.bullets || []).some((b) => typeof b === 'object' && b?.linkTo);
      const hasContent = block.title || block.intro || (block.paragraphs ?? []).length > 0 || hasBullets || block.closing;
      if (!hasContent) return null;
      return (
        <React.Fragment key={index}>
          <ContentSection>
            {block.title != null && String(block.title).trim() && (
              <InfoTitle
                as={motion.h3}
                variants={staggerItem}
              >
                {block.title}
              </InfoTitle>
            )}
            <ContentText
              as={motion.div}
              variants={staggerItem}
            >
              {(block.paragraphs ?? []).map((p, i) => (
                <p key={`bp-${i}`}>{p}</p>
              ))}
              {block.intro != null && String(block.intro).trim() && <p>{block.intro}</p>}
              {hasBullets && (
                (() => {
                  const List = hasLinks ? CenteredDomainLinksList : BulletList;
                  return (
                    <List>
                      {(block.bullets || []).map((b, i) => (
                        <li key={i}>
                          {typeof b === 'object' && b?.linkTo ? (
                            <BulletLink to={b.linkTo}>{b.text}</BulletLink>
                          ) : (
                            typeof b === 'string' ? b : b?.text ?? ''
                          )}
                        </li>
                      ))}
                    </List>
                  );
                })()
              )}
              {block.closing != null && String(block.closing).trim() && <p>{block.closing}</p>}
            </ContentText>
          </ContentSection>
          {showOverviewDivider && <SectionDivider />}
        </React.Fragment>
      );
    }
    if (block.type === 'categoryCardGrid') {
      const items = block.items ?? [];
      if (!items.length) return null;
      return (
        <ContentSection key={index}>
          <CategoryCardGrid as={motion.div} variants={staggerContainer}>
            {items.map((item, i) => (
              <React.Fragment key={item.id || i}>
                <motion.div variants={staggerItem}>
                  <CategoryCard to={item.linkTo}>
                    <CategoryCardTitle>{item.title}</CategoryCardTitle>
                    {item.intro && (
                      Array.isArray(item.intro)
                        ? item.intro.map((p, pi) => <CategoryCardIntro key={pi}>{p}</CategoryCardIntro>)
                        : <CategoryCardIntro>{item.intro}</CategoryCardIntro>
                    )}
                    {(item.bulletsTitle && item.bullets?.length > 0) || (item.recentPublicationsTitle && item.recentPublications?.length > 0) ? (
                      <CategoryCardTwoCol>
                        {item.bulletsTitle && item.bullets?.length > 0 && (
                          <CategoryCardCol>
                            <CategoryCardSectionTitle>{item.bulletsTitle}</CategoryCardSectionTitle>
                            <CategoryCardBulletList>
                              {item.bullets.map((b, bi) => (
                                <li key={bi}>{b}</li>
                              ))}
                            </CategoryCardBulletList>
                          </CategoryCardCol>
                        )}
                        {item.recentPublicationsTitle && item.recentPublications?.length > 0 && (
                          <CategoryCardCol>
                            <CategoryCardSectionTitle>{item.recentPublicationsTitle}</CategoryCardSectionTitle>
                            <CategoryCardPublicationsList>
                              {item.recentPublications.map((pub, pi) => (
                                <li key={pi}>{pub}</li>
                              ))}
                            </CategoryCardPublicationsList>
                          </CategoryCardCol>
                        )}
                      </CategoryCardTwoCol>
                    ) : null}
                    <CategoryCardButton>
                      {item.buttonText}
                      <CardArrowIcon />
                    </CategoryCardButton>
                  </CategoryCard>
                </motion.div>
                <SectionDivider />
              </React.Fragment>
            ))}
          </CategoryCardGrid>
        </ContentSection>
      );
    }
    return null;
  };

  const hasSectionContent = blocks.length > 0;

  return (
    <>
      <SEO
        title={`${title} – APEXGATE GLOBAL`}
        description={subtitle || ''}
        image={heroImage}
      />
      <LazyBackgroundImage
        src={heroImage}
        critical={true}
        rounded={false}
        backgroundPosition="50% 70%"
      >
        <StrategicHeroSection>
          <Container>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={heroStaggerContainerVariants}
              style={{ width: '100%' }}
            >
              <StrategicHeroContent style={{ animation: 'none' }}>
                <HeroTitle as={motion.h1} variants={heroStaggerItemVariants}>
                  {title}
                </HeroTitle>
                {subtitle && (
                  <ContentText
                    as={motion.p}
                    variants={heroStaggerItemVariants}
                    style={{
                      fontSize: 'clamp(16px, 2.5vw, 18px)',
                      color: 'rgba(255, 255, 255, 0.92)',
                      marginTop: '12px',
                      maxWidth: 'min(800px, 100%)',
                      lineHeight: 1.6,
                      textAlign: 'left',
                    }}
                  >
                    {subtitle}
                  </ContentText>
                )}
              </StrategicHeroContent>
            </motion.div>
          </Container>
        </StrategicHeroSection>
      </LazyBackgroundImage>

      {hasSectionContent && (
        <PageContainer style={{ paddingTop: '32px' }}>
          <Container>
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              {blocks.map((block, index) => renderBlock(block, index))}
            </motion.div>
          </Container>
        </PageContainer>
      )}
    </>
  );
};

export default StrategicIntelligence;
